/**
 * Centralized Polling Configuration
 * 
 * Defines safe polling intervals for real-time features.
 * All polling must obey:
 * - networkGate (skip when network is down)
 * - Tab visibility (pause when tab is hidden)
 * 
 * Rules:
 * - Poll only when needed (if user is not in relevant page/popup, do not poll that feature)
 * - All polling must check networkGate + visibility before firing requests
 */

export const POLLING_INTERVALS = {
  /**
   * Notifications count polling
   * Used in: Header badge, notifications panel
   */
  notifications: {
    interval: 20000, // 20 seconds (was 15s)
    enabled: true,
  },
  
  /**
   * Reminders polling
   * Used in: GlobalReminderPopup
   */
  reminders: {
    interval: 30000, // 30 seconds (was 2s - TOO FREQUENT)
    enabled: true,
  },
  
  /**
   * Tasks polling
   * Used in: Task pages, task lists
   */
  tasks: {
    interval: 30000, // 30 seconds
    enabled: true,
  },
  
  /**
   * Heartbeat polling
   * Used in: GlobalReminderPopup heartbeat, presence updates
   */
  heartbeat: {
    interval: 45000, // 45 seconds (was 2s - TOO FREQUENT)
    enabled: true,
  },
  
  /**
   * Emails count polling
   * Used in: Header badge
   */
  emails: {
    interval: 60000, // 60 seconds (unchanged - reasonable)
    enabled: true,
  },
  
  /**
   * Notifications list polling (fallback)
   * Used in: GlobalReminderPopup fallback polling
   */
  notificationsList: {
    interval: 30000, // 30 seconds (unchanged)
    enabled: true,
  },
} as const;

/**
 * Get polling interval for a feature
 */
export function getPollingInterval(feature: keyof typeof POLLING_INTERVALS): number {
  const config = POLLING_INTERVALS[feature];
  return config.enabled ? config.interval : 0;
}

/**
 * Check if polling is enabled for a feature
 */
export function isPollingEnabled(feature: keyof typeof POLLING_INTERVALS): boolean {
  return POLLING_INTERVALS[feature].enabled;
}

